import { toBase64, Constructor } from '../common';
import { GatewayApi } from './base';


export interface IOApi {
    index(path?: string): Promise<any>;
    write(files: File[]): Promise<any>;
    read(path: string, versionId?: string): Promise<any>;
    read_chunk(path: string, index: number, versionId?: string): Promise<any>;
    validateFileSize(path: string): Promise<boolean>;
    generateArchive(key: string, date: string[], source: string): Promise<any>;
    getArchiveStatus(key: string): Promise<ArchiveStatusProps>;
    history(path: string): Promise<any>;
    remove(record: Record<string, any>): Promise<any>;
    isAllowed(method: string): boolean;
}


export interface ApiFile {
    name: string;
    content: string;
    author?: string;
}

export interface ApiFileChunk {
    name: string;
    url: string;
    chunk: string;
    chunkCounts: number;
    index: number;
    author?: string;
}

export interface ApiHistory {
    name: string;
    content: any[];
    author?: string;
}

export interface ArchiveStatusProps {
   status: string;
   files_processed: number;
   files_count: number;
   url: string;
}

export function withIOApi<T extends Constructor<GatewayApi>>(target: T) {
    return class Impl extends target implements IOApi {
        protected allowedMethods: Set<string>;

        constructor(...args: any[]) {
            super(...args);
            this.allowedMethods = new Set(['index', 'remove', 'write', 'read', 'history']);
        }

        async read(path: string, versionId?: string): Promise<ApiFile> {
            const json = await this.invoke('read', { path, versionId });
            const content = atob(json.content);
            const name = path.split('/').reverse()[0];
            return { name, content };
        }

        async read_chunk(url: string, index: number = 1, versionId?: string): Promise<ApiFileChunk> {
            const parsedUrl = new URL(url);
            const path = parsedUrl.pathname.slice(1);

            const json = await this.invoke('read_chunk', {path, index, versionId});

            const chunk = atob(json.chunk);
            const chunkCounts = json.chunk_counts
            const name = path.split('/').reverse()[0];

            return {name, url, chunk, chunkCounts, index};
        }

        async validateFileSize(url: string): Promise<boolean> {
            const parsedUrl = new URL(url);
            const path = parsedUrl.pathname.slice(1);
            const json = await this.invoke('validate_file_size', {path});
            return json.is_exceeds_size_limit
        }

        async generateArchive(key: string, date: string[], source: string): Promise<any> {
            return await this.invoke("generate_archive", {key, date, source});
        }

        async getArchiveStatus(key: string): Promise<ArchiveStatusProps> {
            return await this.invoke("get_archive_status", {key});
        }

        async history(path: string): Promise<ApiHistory> {
            const json = await this.invoke('history', { path });
            const content = json.history;
            const name = path.split('/').reverse()[0];
            return { name, content };
        }

        private async prepare_files(files: File[]) {
            return Promise.all(files.map(
                async file => {
                    const { name } = file;
                    let author = ''
                    let content = (await toBase64(file) as string);
                    content = content.slice(content.indexOf('base64') + 6);

                    if (this.user){
                    author = this.user.name.join();
                    }
                    return { name, content, author };
                }
            ));
        }

        async write(files: File[]) {
            const data = await this.prepare_files(files);
            return this.invoke('write', { data });
        }

        async request_write(files: File[]) {
            const data = await this.prepare_files(files);
            const response = await this.submit_request(`${this.endpoint}/write`, { data });
            return {'request_submitted': response};
        }

        async remove(record: Record<string, any>) {
            return this.invoke('remove', record);
        }

        async index(path: string = '') {
            return this.invoke('index', { path }) ?? [];
        }

        isAllowed(method: string) {
            return this.allowedMethods.has(method);
        }

        async getSqsConfig() {
            const json = await this.invoke('get_sqs_config');
            const content = atob(json.content);
            return {item: json.item[0], content: content, jsonContent: JSON.parse(content)};
        }

        async saveSqsConfig(sqsConfig: {[key: string]: string[]}) {
            let author = ''
            if (this.user) {
                author = this.user.name.join();
            }
            const response = await this.submit_request(
                `${this.endpoint}/${'save_sqs_config'}`,
                {data: {sqsConfig, author}}
            );
            return {'request_submitted': response};
        }
    };
}
